import { format, isValid, parseISO } from 'date-fns'

export function isTokenExpired(expiration: number = 0) {
  const now = Math.floor(Date.now() / 1000)
  return expiration < now
}

export function adjustTextSize(
  containerElement: HTMLElement | null,
  textElement: HTMLElement | null,
  maxFontSizeParam?: number | undefined,
) {
  let minFontSize = 1 // Minimum font size
  let maxFontSize = maxFontSizeParam ? maxFontSizeParam : 170 // Maximum font size, start big enough
  let fontSize

  if (containerElement === null || textElement === null) return
  while (minFontSize <= maxFontSize) {
    fontSize = ((minFontSize + maxFontSize) / 2) | 0 // Integer division
    textElement.style.fontSize = fontSize + 'px'

    if (textElement.scrollWidth <= containerElement.offsetWidth) {
      minFontSize = fontSize + 1 // Try a bigger size
    } else {
      maxFontSize = fontSize - 1 // Try a smaller size
    }
  }

  // Set the font size to the last best fit
  textElement.style.fontSize = minFontSize - 1 + 'px'
}

/**
 * Formats a given date in 'MM/dd/yyyy' format.
 * If `showTimeStamp` is true, it includes the time as 'MM/dd/yyyy HH:mm'.
 * Returns an empty string if the date is null, undefined, or invalid.
 *
 * @param {string | Date | null | undefined} date - The date to format.
 * @param {boolean} [showTimeStamp=false] - Whether to include the time in the format.
 * @returns {string} The formatted date string, or an empty string if the input is invalid.
 *
 * @example
 * formatDate('2025-09-30'); // '09/30/2025'
 * formatDate('2025-09-30T14:30:00', true); // '09/30/2025 14:30'
 * formatDate(new Date(), true); // '02/28/2025 16:45'
 * formatDate(null); // ''
 * formatDate('invalid-date'); // ''
 */
export function formatDate(date?: string | Date | null, showTimeStamp: boolean = false, _format?: string): string {
  if (!date) return '' // Return empty string for null/undefined values

  const parsedDate = date instanceof Date ? date : parseISO(date)

  if (!isValid(parsedDate)) return '' // Return empty string for invalid dates

  const formatString = _format ? _format : showTimeStamp ? 'MM/dd/yyyy HH:mm' : 'MM/dd/yyyy'

  return format(parsedDate, formatString)
}
